import type { BrowserContext } from 'playwright';

import type { BrowserClientOptions } from '../browser-client.interface.js';
import type { BrowserLogger } from '../logger.js';
import { PlaywrightTraceRecorder, type TraceStopResult } from './recording.js';

export type { TraceStopResult };

type PlaywrightTraceAdapterOptions = {
  tmpBaseDir: string;
  logger: BrowserLogger;
  getContext: () => Promise<BrowserContext>;
  getCurrentContext: () => BrowserContext | null;
  now?: () => number;
};

export class PlaywrightTraceAdapter {
  private readonly logger: BrowserLogger;
  private readonly recorder: PlaywrightTraceRecorder;

  constructor(options: PlaywrightTraceAdapterOptions) {
    this.logger = options.logger;
    this.recorder = new PlaywrightTraceRecorder(options);
  }

  isActive(): boolean {
    return this.recorder.isActive();
  }

  get chunkCount(): number {
    return this.recorder.chunkCount;
  }

  async startTracing(_opts?: BrowserClientOptions): Promise<void> {
    this.logger.info('[DirectPlaywright] Starting trace recording');
    await this.recorder.start();
  }

  async stopTracing(_opts?: BrowserClientOptions): Promise<TraceStopResult> {
    this.logger.info('[DirectPlaywright] Stopping trace recording');
    return this.recorder.stop();
  }

  traceGroupDepth(): number {
    return this.recorder.traceGroupDepth();
  }

  async traceGroup(name: string): Promise<void> {
    await this.recorder.traceGroup(name);
  }

  async traceGroupEnd(): Promise<void> {
    await this.recorder.traceGroupEnd();
  }

  async traceGroupEndUntil(depth: number): Promise<void> {
    await this.recorder.traceGroupEndUntil(depth);
  }

  async flushCurrentChunk(): Promise<string | null> {
    return this.recorder.flushCurrentChunk();
  }

  async beginOnContext(): Promise<void> {
    await this.recorder.beginOnContext();
  }

  markInactive(): void {
    this.recorder.markInactive();
  }

  async resumeAfterRecreatedContext(): Promise<void> {
    await this.recorder.resumeAfterRecreatedContext();
  }

  async cleanupTraceDirectories(): Promise<void> {
    await this.recorder.cleanupTraceDirectories();
  }
}
